import { Link, useLocation } from "wouter";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupAction,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuAction,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  SidebarSeparator,
  useSidebar,
} from "@/components/ui/sidebar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  MessageSquare,
  Church,
  Brain,
  BarChart3,
  GraduationCap,
  Crown,
  Sun,
  Moon,
  LogIn,
  LogOut,
  User,
  Heart,
  Cross,
  HandHeart,
  School,
  KeyRound,
  LifeBuoy,
  Shield,
  ShieldCheck,
  FileText,
  Trash2,
  Users,
  Scroll,
  Receipt as ReceiptIcon,
  Sparkles,
  Plus,
  FolderPlus,
  Folder,
  MoreHorizontal,
  Pencil,
  FolderInput,
  Check,
  X as XIcon,
  Download,
} from "lucide-react";
import { openStripePaymentLink, showStripeButtons } from "@/lib/stripe-payment-links";
import { STARGATE_ONLINE } from "@shared/models/chat";
import { BRAND } from "@/config/brand";
import { useTheme } from "@/components/theme-provider";
import { useProfile } from "@/contexts/ProfileContext";
import { useAuth } from "@/hooks/use-auth";
import { useEntitlement } from "@/hooks/use-entitlement";
import { useGlobalCounters } from "@/hooks/use-global-counters";
import { cn } from "@/lib/utils";
import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { NotificationBell } from "@/components/NotificationBell";
import { ProfileModal } from "@/components/ProfileModal";
import { TitheModal } from "./TitheModal";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useToast } from "@/hooks/use-toast";
import type { ChatThread } from "@/components/chat/types";

const NAV_ITEMS = [
  { label: "Chat with Memnora", href: "/", icon: MessageSquare, tag: "chat" },
  { label: "Sacred History & Books", href: "/temples", icon: Church, tag: "temples" },
  { label: "Sacred Text", href: "/sacred-text", icon: Scroll, tag: "sacred-text" },
  { label: "Resonance Memory", href: "/memory", icon: Brain, tag: "memory" },
  { label: "Discipleship Q&A", href: "/qa", icon: BarChart3, tag: "qa", badge: "FREE" },
];

const STUDY_ITEMS = [
  { label: "Bible Curriculum", href: "/bible-study/k12", icon: GraduationCap, tag: "k12" },
  { label: "Adult Bible Study", href: "/bible-study/adult", icon: Crown, tag: "adult" },
];

const OWNER_PROFILE_ID = "ed3a28de-7588-4809-bba9-11314dc6099b";

interface ChatProject {
  id: string;
  name: string;
  threadCount: number;
  updatedAt: string;
}

export function AppSidebar() {
  const [location] = useLocation();
  const { theme, setTheme } = useTheme();
  const { profile, signOut, setProfile } = useProfile();
  const { isAuthenticated } = useAuth();
  const { data: entitlement } = useEntitlement();
  const { savedUnique, heartUnique, visitorsUnique, recordSaved, recordHeart, isPending } = useGlobalCounters();
  const { isMobile, setOpenMobile } = useSidebar();
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [profileModalTab, setProfileModalTab] = useState<string>("login");
  const [titheOpen, setTitheOpen] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deletePassword, setDeletePassword] = useState("");
  const { toast } = useToast();
  const [, setLocation] = useLocation();
  const queryClient = useQueryClient();

  const [showNewProjectDialog, setShowNewProjectDialog] = useState(false);
  const [newProjectName, setNewProjectName] = useState("");
  const [renamingThreadId, setRenamingThreadId] = useState<string | null>(null);
  const [renamingProjectId, setRenamingProjectId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState("");

  const { data: mySchools } = useQuery<Array<{ id: string; name: string; membership: { role: string } }>>({
    queryKey: ["/api/schools/my-schools"],
    enabled: !!profile,
  });

  const { data: allThreads } = useQuery<ChatThread[]>({
    queryKey: ["/api/magnora/threads", profile?.id],
    queryFn: async () => {
      const res = await fetch("/api/magnora/threads", { credentials: "include" });
      if (!res.ok) throw new Error("Failed to load conversations");
      return res.json();
    },
    enabled: isAuthenticated,
  });

  const { data: projects } = useQuery<ChatProject[]>({
    queryKey: ["/api/magnora/projects"],
    enabled: isAuthenticated,
  });

  const currentThreadId =
    typeof window !== "undefined" ? sessionStorage.getItem("memnoraThreadId") : null;

  const selectThread = (threadId: string) => {
    sessionStorage.setItem("memnoraThreadId", threadId);
    window.dispatchEvent(new CustomEvent("memnora:select-thread", { detail: threadId }));
    setLocation("/chat");
    handleNav();
  };

  const startNewChat = () => {
    sessionStorage.removeItem("memnoraThreadId");
    window.dispatchEvent(new Event("memnora:new-chat"));
    setLocation("/chat");
    handleNav();
  };

  const createProject = useMutation({
    mutationFn: async (name: string) => {
      const res = await apiRequest("POST", "/api/magnora/projects", { name });
      return await res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/magnora/projects"] });
      setShowNewProjectDialog(false);
      setNewProjectName("");
    },
    onError: (err: Error) => {
      toast({ title: "Couldn't create project", description: err.message, variant: "destructive" });
    },
  });

  const renameProject = useMutation({
    mutationFn: async ({ id, name }: { id: string; name: string }) => {
      await apiRequest("PATCH", `/api/magnora/projects/${id}`, { name });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/magnora/projects"] });
      setRenamingProjectId(null);
    },
    onError: (err: Error) => {
      toast({ title: "Couldn't rename project", description: err.message, variant: "destructive" });
    },
  });

  const deleteProject = useMutation({
    mutationFn: async (id: string) => {
      await apiRequest("DELETE", `/api/magnora/projects/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/magnora/projects"] });
      queryClient.invalidateQueries({ queryKey: ["/api/magnora/threads"] });
    },
    onError: (err: Error) => {
      toast({ title: "Couldn't delete project", description: err.message, variant: "destructive" });
    },
  });

  const updateThread = useMutation({
    mutationFn: async ({ id, ...body }: { id: string; title?: string; projectId?: string | null }) => {
      await apiRequest("PATCH", `/api/magnora/threads/${id}`, body);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/magnora/threads"] });
      setRenamingThreadId(null);
    },
    onError: (err: Error) => {
      toast({ title: "Couldn't update conversation", description: err.message, variant: "destructive" });
    },
  });

  const deleteThread = useMutation({
    mutationFn: async (id: string) => {
      await apiRequest("DELETE", `/api/magnora/threads/${id}`);
    },
    onSuccess: (_data, id) => {
      queryClient.invalidateQueries({ queryKey: ["/api/magnora/threads"] });
      if (currentThreadId === id) startNewChat();
    },
    onError: (err: Error) => {
      toast({ title: "Couldn't delete conversation", description: err.message, variant: "destructive" });
    },
  });

  const unfiledThreads = (allThreads ?? []).filter((t) => !t.projectId);
  const threadsByProject = new Map<string, ChatThread[]>();
  for (const t of allThreads ?? []) {
    if (!t.projectId) continue;
    const list = threadsByProject.get(t.projectId) ?? [];
    list.push(t);
    threadsByProject.set(t.projectId, list);
  }

  const isActive = (href: string) => {
    if (href === "/") return location === "/" || location === "/chat" || location === "/memnora" || location === "/home";
    return location.startsWith(href);
  };

  const handleNav = () => {
    if (isMobile) setOpenMobile(false);
  };

  return (
    <>
      <Sidebar collapsible="icon" className="border-r">
        <SidebarHeader className="p-4">
          <div className="flex items-center gap-3 group-data-[collapsible=icon]:justify-center">
            <div className="flex items-center justify-center h-8 w-8 rounded-md bg-amber-600 text-white font-bold text-sm flex-shrink-0" data-testid="logo-sidebar">
              T
            </div>
            <span className="font-semibold text-sm truncate group-data-[collapsible=icon]:hidden flex-1" data-testid="text-sidebar-title">
              {BRAND.platform}
            </span>
            <div className="group-data-[collapsible=icon]:hidden">
              <NotificationBell />
            </div>
          </div>
        </SidebarHeader>

        <SidebarSeparator />

        <SidebarContent>
          {isAuthenticated && (
            <>
              <SidebarGroup>
                <SidebarGroupLabel>Chats</SidebarGroupLabel>
                <SidebarGroupAction
                  title="New project"
                  onClick={() => setShowNewProjectDialog(true)}
                  data-testid="button-new-project"
                >
                  <FolderPlus className="h-4 w-4" />
                </SidebarGroupAction>
                <SidebarGroupContent>
                  <SidebarMenu>
                    <SidebarMenuItem>
                      <SidebarMenuButton onClick={startNewChat} tooltip="New chat" data-testid="button-new-chat">
                        <Plus className="h-4 w-4" />
                        <span>New chat</span>
                      </SidebarMenuButton>
                    </SidebarMenuItem>

                    {(projects ?? []).map((project) => (
                      <SidebarMenuItem key={project.id}>
                        {renamingProjectId === project.id ? (
                          <div className="flex items-center gap-1 px-2 py-1">
                            <Input
                              autoFocus
                              value={renameValue}
                              onChange={(e) => setRenameValue(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === "Enter" && renameValue.trim()) {
                                  renameProject.mutate({ id: project.id, name: renameValue.trim() });
                                } else if (e.key === "Escape") {
                                  setRenamingProjectId(null);
                                }
                              }}
                              className="h-7 text-xs"
                              data-testid={`input-rename-project-${project.id}`}
                            />
                            <Button
                              size="icon"
                              variant="ghost"
                              className="h-6 w-6"
                              onClick={() => renameValue.trim() && renameProject.mutate({ id: project.id, name: renameValue.trim() })}
                            >
                              <Check className="h-3.5 w-3.5" />
                            </Button>
                            <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => setRenamingProjectId(null)}>
                              <XIcon className="h-3.5 w-3.5" />
                            </Button>
                          </div>
                        ) : (
                          <>
                            <SidebarMenuButton tooltip={project.name} data-testid={`nav-project-${project.id}`}>
                              <Folder className="h-4 w-4" />
                              <span className="truncate flex-1">{project.name}</span>
                              <span className="text-[10px] text-muted-foreground">{project.threadCount}</span>
                            </SidebarMenuButton>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <SidebarMenuAction data-testid={`button-project-menu-${project.id}`}>
                                  <MoreHorizontal className="h-4 w-4" />
                                </SidebarMenuAction>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent side="right" align="start">
                                <DropdownMenuItem
                                  onClick={() => { setRenamingProjectId(project.id); setRenameValue(project.name); }}
                                  data-testid={`button-rename-project-${project.id}`}
                                >
                                  <Pencil className="h-4 w-4 mr-2" /> Rename
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                  className="text-destructive"
                                  onClick={() => deleteProject.mutate(project.id)}
                                  data-testid={`button-delete-project-${project.id}`}
                                >
                                  <Trash2 className="h-4 w-4 mr-2" /> Delete project
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </>
                        )}
                        {(threadsByProject.get(project.id) ?? []).length > 0 && (
                          <SidebarMenuSub>
                            {(threadsByProject.get(project.id) ?? []).map((thread) => (
                              <SidebarMenuSubItem key={thread.id}>
                                <SidebarMenuSubButton
                                  isActive={currentThreadId === thread.id}
                                  onClick={() => selectThread(thread.id)}
                                  className="cursor-pointer"
                                  data-testid={`nav-thread-${thread.id}`}
                                >
                                  <span className="truncate">{thread.title}</span>
                                </SidebarMenuSubButton>
                              </SidebarMenuSubItem>
                            ))}
                          </SidebarMenuSub>
                        )}
                      </SidebarMenuItem>
                    ))}

                    {unfiledThreads.length === 0 && (!projects || projects.length === 0) ? (
                      <div className="px-3 py-2 text-xs text-muted-foreground" data-testid="text-no-threads">
                        No conversations yet
                      </div>
                    ) : (
                      unfiledThreads.map((thread) => (
                        <SidebarMenuItem key={thread.id}>
                          {renamingThreadId === thread.id ? (
                            <div className="flex items-center gap-1 px-2 py-1">
                              <Input
                                autoFocus
                                value={renameValue}
                                onChange={(e) => setRenameValue(e.target.value)}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter" && renameValue.trim()) {
                                    updateThread.mutate({ id: thread.id, title: renameValue.trim() });
                                  } else if (e.key === "Escape") {
                                    setRenamingThreadId(null);
                                  }
                                }}
                                className="h-7 text-xs"
                                data-testid={`input-rename-thread-${thread.id}`}
                              />
                              <Button
                                size="icon"
                                variant="ghost"
                                className="h-6 w-6"
                                onClick={() => renameValue.trim() && updateThread.mutate({ id: thread.id, title: renameValue.trim() })}
                              >
                                <Check className="h-3.5 w-3.5" />
                              </Button>
                              <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => setRenamingThreadId(null)}>
                                <XIcon className="h-3.5 w-3.5" />
                              </Button>
                            </div>
                          ) : (
                            <>
                              <SidebarMenuButton
                                isActive={currentThreadId === thread.id}
                                onClick={() => selectThread(thread.id)}
                                tooltip={thread.title}
                                data-testid={`nav-thread-${thread.id}`}
                              >
                                <MessageSquare className="h-4 w-4" />
                                <span className="truncate">{thread.title}</span>
                              </SidebarMenuButton>
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <SidebarMenuAction data-testid={`button-thread-menu-${thread.id}`}>
                                    <MoreHorizontal className="h-4 w-4" />
                                  </SidebarMenuAction>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent side="right" align="start">
                                  <DropdownMenuItem
                                    onClick={() => { setRenamingThreadId(thread.id); setRenameValue(thread.title); }}
                                    data-testid={`button-rename-thread-${thread.id}`}
                                  >
                                    <Pencil className="h-4 w-4 mr-2" /> Rename
                                  </DropdownMenuItem>
                                  {projects && projects.length > 0 && (
                                    <DropdownMenuSub>
                                      <DropdownMenuSubTrigger data-testid={`button-move-thread-${thread.id}`}>
                                        <FolderInput className="h-4 w-4 mr-2" /> Move to project
                                      </DropdownMenuSubTrigger>
                                      <DropdownMenuSubContent>
                                        {projects.map((project) => (
                                          <DropdownMenuItem
                                            key={project.id}
                                            onClick={() => updateThread.mutate({ id: thread.id, projectId: project.id })}
                                            data-testid={`button-move-thread-${thread.id}-to-${project.id}`}
                                          >
                                            <Folder className="h-4 w-4 mr-2" /> {project.name}
                                          </DropdownMenuItem>
                                        ))}
                                      </DropdownMenuSubContent>
                                    </DropdownMenuSub>
                                  )}
                                  <DropdownMenuSeparator />
                                  <DropdownMenuItem
                                    className="text-destructive"
                                    onClick={() => deleteThread.mutate(thread.id)}
                                    data-testid={`button-delete-thread-${thread.id}`}
                                  >
                                    <Trash2 className="h-4 w-4 mr-2" /> Delete
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </>
                          )}
                        </SidebarMenuItem>
                      ))
                    )}
                  </SidebarMenu>
                </SidebarGroupContent>
              </SidebarGroup>

              <SidebarSeparator />
            </>
          )}

          <SidebarGroup>
            <SidebarGroupLabel>Navigate</SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                {NAV_ITEMS.map((item) => (
                  <SidebarMenuItem key={item.tag}>
                    <SidebarMenuButton
                      asChild
                      isActive={isActive(item.href)}
                      tooltip={item.label}
                      data-testid={`nav-${item.tag}`}
                    >
                      <Link href={item.href} onClick={handleNav}>
                        <item.icon className="h-4 w-4" />
                        <span className="flex items-center gap-1.5">
                          {item.label}
                          {"badge" in item && item.badge && (
                            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-bold leading-none bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 group-data-[collapsible=icon]:hidden">
                              {item.badge}
                            </span>
                          )}
                        </span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                ))}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>

          <SidebarSeparator />

          <SidebarGroup>
            <SidebarGroupLabel>Bible Study</SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                {STUDY_ITEMS.map((item) => (
                  <SidebarMenuItem key={item.tag}>
                    <SidebarMenuButton
                      asChild
                      isActive={isActive(item.href)}
                      tooltip={item.label}
                      data-testid={`nav-${item.tag}`}
                    >
                      <Link href={item.href} onClick={handleNav}>
                        <item.icon className="h-4 w-4" />
                        <span>{item.label}</span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                ))}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>

          <SidebarSeparator />

          <SidebarGroup>
            <SidebarGroupLabel>School Portal</SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                {mySchools && mySchools.length > 0 ? (
                  mySchools.map((entry) => (
                    <SidebarMenuItem key={entry.id}>
                      <SidebarMenuButton
                        asChild
                        isActive={isActive(`/school/${entry.id}`)}
                        tooltip={entry.name}
                        data-testid={`nav-school-${entry.id}`}
                      >
                        <Link
                          href={entry.membership.role === "ADMIN" || entry.membership.role === "CURRICULUM_LEAD"
                            ? `/school/${entry.id}/admin`
                            : `/school/${entry.id}/teaching`}
                          onClick={handleNav}
                        >
                          <School className="h-4 w-4" />
                          <span className="truncate">{entry.name}</span>
                        </Link>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  ))
                ) : (
                  <SidebarMenuItem>
                    <SidebarMenuButton
                      asChild
                      isActive={isActive("/school/join")}
                      tooltip="Join or Create School"
                      data-testid="nav-school-join"
                    >
                      <Link href="/school/join" onClick={handleNav}>
                        <KeyRound className="h-4 w-4" />
                        <span>Join or Create School</span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                )}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>

          <SidebarSeparator />

          <SidebarGroup>
            <SidebarGroupLabel>Global Testimony</SidebarGroupLabel>
            <SidebarGroupContent>
              <div className="px-2 space-y-2 group-data-[collapsible=icon]:px-0">
                <button
                  onClick={recordSaved}
                  disabled={isPending}
                  className={cn(
                    "w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors",
                    "hover:bg-accent/50 cursor-pointer",
                    "group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:px-0"
                  )}
                  data-testid="button-sidebar-salvation"
                >
                  <Cross className={cn("h-5 w-5 flex-shrink-0", savedUnique > 0 ? "text-amber-500 fill-current" : "text-muted-foreground")} />
                  <div className="flex flex-col items-start group-data-[collapsible=icon]:hidden">
                    <span className="text-xs font-medium">Saved in Christ</span>
                    <span className="text-lg font-bold tabular-nums leading-tight" data-testid="text-sidebar-salvation-count">
                      {savedUnique.toLocaleString()}
                    </span>
                  </div>
                </button>

                <button
                  onClick={recordHeart}
                  disabled={isPending}
                  className={cn(
                    "w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors",
                    "hover:bg-accent/50 cursor-pointer",
                    "group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:px-0"
                  )}
                  data-testid="button-sidebar-heart"
                >
                  <Heart className={cn("h-5 w-5 flex-shrink-0", heartUnique > 0 ? "text-red-500 fill-current" : "text-muted-foreground")} />
                  <div className="flex flex-col items-start group-data-[collapsible=icon]:hidden">
                    <span className="text-xs font-medium">Hearts Lifted</span>
                    <span className="text-lg font-bold tabular-nums leading-tight" data-testid="text-sidebar-heart-count">
                      {heartUnique.toLocaleString()}
                    </span>
                  </div>
                </button>

                <div
                  className={cn(
                    "w-full flex items-center gap-3 px-3 py-2.5 rounded-lg",
                    "group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:px-0"
                  )}
                  data-testid="display-sidebar-visitors"
                >
                  <Users className="h-5 w-5 flex-shrink-0 text-blue-400" />
                  <div className="flex flex-col items-start group-data-[collapsible=icon]:hidden">
                    <span className="text-xs font-medium">Souls Reached</span>
                    <span className="text-lg font-bold tabular-nums leading-tight" data-testid="text-sidebar-visitors-count">
                      {visitorsUnique.toLocaleString()}
                    </span>
                  </div>
                </div>

                {showStripeButtons() && STARGATE_ONLINE && (
                  <button
                    onClick={() => openStripePaymentLink("stargateControlTowerLifetime")}
                    className={cn(
                      "w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors",
                      "hover:bg-accent/50 cursor-pointer",
                      "group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:px-0"
                    )}
                    data-testid="button-sidebar-stargate-unlock"
                  >
                    <Sparkles className="h-5 w-5 flex-shrink-0 text-amber-500" />
                    <div className="flex flex-col items-start group-data-[collapsible=icon]:hidden">
                      <span className="text-xs font-medium">Unlock Stargate Control Tower</span>
                      <span className="text-[10px] text-muted-foreground">$10 Lifetime Access</span>
                    </div>
                  </button>
                )}

                {STARGATE_ONLINE && (
                  <button
                    onClick={() => setTitheOpen(true)}
                    className={cn(
                      "w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors",
                      "hover:bg-accent/50 cursor-pointer",
                      "group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:px-0"
                    )}
                    data-testid="button-sidebar-tithe"
                  >
                    <HandHeart className="h-5 w-5 flex-shrink-0 text-amber-500" />
                    <span className="text-xs font-medium group-data-[collapsible=icon]:hidden">Tithes & Offerings</span>
                  </button>
                )}

                {profile && (
                  <Link
                    href="/giving/receipts"
                    onClick={handleNav}
                    className={cn(
                      "w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors",
                      "hover:bg-accent/50 cursor-pointer",
                      isActive("/giving/receipts") && "bg-accent",
                      "group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:px-0"
                    )}
                    data-testid="nav-giving-receipts"
                  >
                    <ReceiptIcon className="h-5 w-5 flex-shrink-0 text-amber-500" />
                    <span className="text-xs font-medium group-data-[collapsible=icon]:hidden">Giving History</span>
                  </Link>
                )}

                <Link
                  href="/command-center"
                  onClick={handleNav}
                  className={cn(
                    "w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors",
                    "hover:bg-accent/50 cursor-pointer",
                    (isActive("/command-center") || isActive("/giving/analytics") || isActive("/war-room")) && "bg-accent",
                    "group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:px-0"
                  )}
                  data-testid="nav-command-center"
                >
                  <BarChart3 className="h-5 w-5 flex-shrink-0 text-amber-500" />
                  <span className="text-xs font-medium group-data-[collapsible=icon]:hidden">Command Center</span>
                </Link>
              </div>
            </SidebarGroupContent>
          </SidebarGroup>

          <SidebarSeparator />

          <SidebarGroup>
            <SidebarGroupLabel>Settings & Support</SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                <SidebarMenuItem>
                  <SidebarMenuButton
                    onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
                    tooltip={theme === "dark" ? "Light mode" : "Dark mode"}
                    data-testid="button-sidebar-theme"
                  >
                    {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
                    <span>{theme === "dark" ? "Light Mode" : "Dark Mode"}</span>
                  </SidebarMenuButton>
                </SidebarMenuItem>
                <SidebarMenuItem>
                  <SidebarMenuButton
                    asChild
                    isActive={isActive("/support")}
                    tooltip="Support & Contact"
                    data-testid="nav-support"
                  >
                    <Link href="/support" onClick={handleNav}>
                      <LifeBuoy className="h-4 w-4" />
                      <span>Support & Contact</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
                <SidebarMenuItem>
                  <SidebarMenuButton
                    asChild
                    isActive={isActive("/privacy")}
                    tooltip="Privacy Policy"
                    data-testid="nav-privacy"
                  >
                    <Link href="/privacy" onClick={handleNav}>
                      <Shield className="h-4 w-4" />
                      <span>Privacy Policy</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
                <SidebarMenuItem>
                  <SidebarMenuButton
                    asChild
                    isActive={isActive("/terms")}
                    tooltip="Terms of Service"
                    data-testid="nav-terms"
                  >
                    <Link href="/terms" onClick={handleNav}>
                      <FileText className="h-4 w-4" />
                      <span>Terms of Service</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
                {profile && (
                  <SidebarMenuItem>
                    <SidebarMenuButton
                      asChild
                      isActive={isActive("/account/security")}
                      tooltip="Account Security (2FA)"
                      data-testid="nav-account-security"
                    >
                      <Link href="/account/security" onClick={handleNav}>
                        <ShieldCheck className="h-4 w-4" />
                        <span>Account Security</span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                )}
                {profile && (
                  <SidebarMenuItem>
                    <SidebarMenuButton
                      onClick={() => setShowDeleteDialog(true)}
                      tooltip="Delete Account"
                      data-testid="button-sidebar-delete-account"
                    >
                      <Trash2 className="h-4 w-4 text-destructive" />
                      <span className="text-destructive">Delete Account</span>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                )}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        </SidebarContent>

        <SidebarFooter className="p-2">
          {profile ? (
            <div className="rounded-lg border bg-card p-3 space-y-3 group-data-[collapsible=icon]:p-1">
              <div className="flex items-center gap-3 group-data-[collapsible=icon]:justify-center">
                <div className="h-10 w-10 rounded-full bg-amber-600/20 flex items-center justify-center flex-shrink-0 ring-2 ring-amber-600/40">
                  <User className="h-5 w-5 text-amber-500" />
                </div>
                <div className="flex-1 min-w-0 group-data-[collapsible=icon]:hidden">
                  <p className="text-sm font-semibold truncate" data-testid="text-sidebar-username">
                    {profile.displayName}
                  </p>
                  <div className="flex items-center gap-1 mt-0.5">
                    <Badge variant="secondary" className="text-[10px] px-1.5 py-0" data-testid="badge-sidebar-status">
                      {profile.isSaved ? "Saved in Christ" : "Member"}
                    </Badge>
                    {entitlement && entitlement.tier !== "free" && (
                      <Badge variant="outline" className="text-[10px] px-1.5 py-0 border-amber-500/50 text-amber-500" data-testid="badge-sidebar-premium">
                        {entitlement.founding ? "Founding" : entitlement.tier === "pro" ? "Pro" : "Plus"}
                      </Badge>
                    )}
                  </div>
                </div>
              </div>
              <div className="group-data-[collapsible=icon]:hidden space-y-2">
                {entitlement?.limits.dataExport ? (
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full"
                    onClick={() => window.open("/api/magnora/export?format=txt", "_blank")}
                    data-testid="button-sidebar-export"
                  >
                    <Download className="h-4 w-4 mr-2" />
                    Export conversations
                  </Button>
                ) : (
                  // Every non-Pro account gets one persistent, always-visible
                  // upgrade affordance here — the ChatGPT-style "Upgrade
                  // plan" entry point next to the account card, rather than
                  // only surfacing this reactively when a limit is hit.
                  <Button
                    variant="default"
                    size="sm"
                    className="w-full bg-amber-500 text-black hover:bg-amber-400"
                    onClick={() => setLocation("/pricing#membership")}
                    data-testid="button-sidebar-upgrade"
                  >
                    <Sparkles className="h-4 w-4 mr-2" />
                    {entitlement?.tier === "plus" ? "Upgrade to Pro" : "Upgrade to Memnora+"}
                  </Button>
                )}
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full text-destructive hover:text-destructive border-destructive/30 hover:border-destructive/50 hover:bg-destructive/10"
                  onClick={() => {
                    signOut();
                    if (isMobile) setOpenMobile(false);
                  }}
                  data-testid="button-sidebar-signout"
                >
                  <LogOut className="h-4 w-4 mr-2" />
                  Sign Out
                </Button>
              </div>
            </div>
          ) : (
            <div className="rounded-lg border bg-card p-3 space-y-3 group-data-[collapsible=icon]:p-1">
              <div className="flex items-center gap-3 group-data-[collapsible=icon]:justify-center">
                <div className="h-10 w-10 rounded-full bg-muted flex items-center justify-center flex-shrink-0">
                  <User className="h-5 w-5 text-muted-foreground" />
                </div>
                <div className="flex-1 min-w-0 group-data-[collapsible=icon]:hidden">
                  <p className="text-sm font-semibold">Guest</p>
                  <p className="text-xs text-muted-foreground">Sign in to save your journey</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2 group-data-[collapsible=icon]:hidden">
                <Button
                  variant="default"
                  size="sm"
                  className="w-full"
                  onClick={() => {
                    setShowProfileModal(true);
                    setProfileModalTab("login");
                  }}
                  data-testid="button-sidebar-signin"
                >
                  <LogIn className="h-4 w-4 mr-1" />
                  Sign In
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full"
                  onClick={() => {
                    setShowProfileModal(true);
                    setProfileModalTab("register");
                  }}
                  data-testid="button-sidebar-register"
                >
                  Register
                </Button>
              </div>
            </div>
          )}
        </SidebarFooter>
      </Sidebar>

      {showProfileModal && (
        <ProfileModal
          defaultTab={profileModalTab}
          onClose={() => setShowProfileModal(false)}
        />
      )}
      <TitheModal open={titheOpen} onOpenChange={setTitheOpen} />

      <Dialog open={showNewProjectDialog} onOpenChange={(open) => { setShowNewProjectDialog(open); if (!open) setNewProjectName(""); }}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>New project</DialogTitle>
            <DialogDescription>Group related conversations together.</DialogDescription>
          </DialogHeader>
          <Input
            autoFocus
            placeholder="Project name"
            value={newProjectName}
            onChange={(e) => setNewProjectName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && newProjectName.trim()) createProject.mutate(newProjectName.trim());
            }}
            data-testid="input-new-project-name"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewProjectDialog(false)} data-testid="button-cancel-new-project">
              Cancel
            </Button>
            <Button
              disabled={!newProjectName.trim() || createProject.isPending}
              onClick={() => createProject.mutate(newProjectName.trim())}
              data-testid="button-create-project"
            >
              {createProject.isPending ? "Creating…" : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-destructive">
              <Trash2 className="h-5 w-5" />
              Delete Your Account
            </DialogTitle>
            <DialogDescription>
              This action is permanent and cannot be undone. All your data will be deleted, including:
            </DialogDescription>
          </DialogHeader>
          <ul className="list-disc list-inside space-y-1 text-sm text-muted-foreground px-2">
            <li>Your profile and account credentials</li>
            <li>All chat conversations and memory</li>
            <li>Bible study progress and Q&A responses</li>
            <li>Gallery uploads</li>
            <li>School memberships</li>
          </ul>
          <div className="px-2 space-y-2">
            <p className="text-sm text-muted-foreground">
              If you have an active subscription, it will be cancelled automatically.
            </p>
            <label className="text-sm font-medium" htmlFor="delete-password">
              Enter your password to confirm:
            </label>
            <Input
              id="delete-password"
              type="password"
              placeholder="Your password"
              value={deletePassword}
              onChange={(e) => setDeletePassword(e.target.value)}
              data-testid="input-delete-password"
            />
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => { setShowDeleteDialog(false); setDeletePassword(""); }} data-testid="button-cancel-delete">
              Cancel
            </Button>
            <Button
              variant="destructive"
              disabled={isDeleting || !deletePassword}
              onClick={async () => {
                setIsDeleting(true);
                try {
                  const res = await fetch("/api/auth/delete-account", {
                    method: "DELETE",
                    credentials: "include",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ password: deletePassword }),
                  });
                  if (!res.ok) {
                    const body = await res.json().catch(() => ({}));
                    throw new Error(body.message || "Deletion failed");
                  }
                  setProfile(null);
                  localStorage.removeItem("memnora-profile");
                  localStorage.removeItem("memnora-profile-id");
                  toast({
                    title: "Account Deleted",
                    description: "Your account and all data have been permanently removed.",
                  });
                  setShowDeleteDialog(false);
                  setDeletePassword("");
                  setLocation("/");
                } catch (err: any) {
                  toast({
                    title: "Error",
                    description: err?.message || "Could not delete account. Please contact support.",
                    variant: "destructive",
                  });
                } finally {
                  setIsDeleting(false);
                }
              }}
              data-testid="button-confirm-delete"
            >
              {isDeleting ? "Deleting..." : "Permanently Delete My Account"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
